import { useState, useEffect } from "react";

const MIN_CHARS = 3;
const DEBOUNCE_MS = 300;

/**
 * Microsoft Graph people search hook.
 *
 * Fires automatically after MIN_CHARS characters are typed, with a DEBOUNCE_MS
 * debounce. Each fetch is cancellable via AbortController so stale responses
 * are discarded on rapid input changes.
 *
 * @param {string} query - The current search term (auto-fires when >= MIN_CHARS).
 * @returns {{ rows: Array, isLoading: boolean, error: string|null, hasSearched: boolean }}
 *
 * Each row (normalised to the same flat shape as usePhoneSearch rows):
 *   { recordId, recordType: "MSUser", name, company, phone, label,
 *     source: "microsoft", _raw: graphUser }
 */
export function useMicrosoftSearch(query) {
  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasSearched, setHasSearched] = useState(false);

  useEffect(() => {
    if (!query || query.length < MIN_CHARS) {
      setRows([]);
      setIsLoading(false);
      setError(null);
      setHasSearched(false);
      return;
    }

    let controller;

    const timerId = setTimeout(() => {
      controller = new AbortController();
      setIsLoading(true);
      setError(null);
      setRows([]);
      setHasSearched(false);

      fetch(`/api/users?q=${encodeURIComponent(query)}`, {
        credentials: "include",
        signal: controller.signal,
      })
        .then((res) => {
          if (res.status === 401) throw new Error("Session expired. Please log in again.");
          if (!res.ok) throw new Error("Failed to fetch users");
          return res.json();
        })
        .then((data) => {
          const users = Array.isArray(data) ? data : [];
          const flatRows = [];

          for (const user of users) {
            const base = {
              recordId: user.id,
              recordType: "MSUser",
              name: user.displayName || user.mail || "",
              company: user.jobTitle || user.department || "",
              source: "microsoft",
              _raw: user,
            };

            const phones = [];
            if (Array.isArray(user.businessPhones)) {
              user.businessPhones.forEach((p) => {
                if (p) phones.push({ phone: p, label: "Business Phone" });
              });
            }
            if (user.mobilePhone) {
              phones.push({ phone: user.mobilePhone, label: "Mobile Phone" });
            }

            if (phones.length > 0) {
              phones.forEach(({ phone, label }) => flatRows.push({ ...base, phone, label }));
            } else {
              // Include users without a phone — useful for Teams-only transfers
              flatRows.push({ ...base, phone: "", label: "MS Teams" });
            }
          }

          setRows(flatRows);
          setHasSearched(true);
          setIsLoading(false);
        })
        .catch((err) => {
          if (err.name === "AbortError") return;
          console.error("[useMicrosoftSearch]", err);
          setError(err.message || "Failed to fetch users");
          setRows([]);
          setHasSearched(true);
          setIsLoading(false);
        });
    }, DEBOUNCE_MS);

    return () => {
      clearTimeout(timerId);
      if (controller) controller.abort();
    };
  }, [query]);

  return { rows, isLoading, error, hasSearched };
}
